
import React, { useEffect, useState } from 'react';
import { Box, TableBody } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  MainContainer,
  HeaderContainer,
  PageTitle,
  CreateButton,
  StyledTableContainer,
  StyledTable,
  StyledTableHead,
  StyledTableRow,
  StyledTableCell,
  ActionButton,
  DeleteIconWrapper
} from './styles';
import CustomPagination from '../../../components/CustomPagination';
import { useBreadcrumb } from '../../../context/BreadcrumbContext';
import { useSidebar } from '../../../context/SidebarContext';
import QualificationModal from './components/QualificationModal';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../store';
import { searchQualifications, deleteQualification } from '../../../modules/masterdata/actions';
import {
  selectQualificationsList,
  selectSearchQualificationsCount,
  selectCreateQualificationSuccess,
  selectCreateQualificationError,
  selectUpdateQualificationSuccess,
  selectUpdateQualificationError,
  selectDeleteQualificationSuccess,
  selectDeleteQualificationError
} from '../../../modules/masterdata/selectors';
import {
  resetQualificationState,
  resetUpdateState,
  resetDeleteState
} from '../../../modules/masterdata/reducers';
import { IQualification } from '../../../modules/masterdata/types';
import AlertPopup from '../../../components/common/AlertPopup';
import ConfirmationDialog from '../../../components/common/ConfirmationDialog'; // Assuming this was added earlier, but I don't see it in file content step 493! Wait, step 493 showed imports ending at line 27. It didn't show ConfirmationDialog import. 
// Ah, step 423 failed. So ConfirmationDialog might be missing too!
// I need to add ConfirmationDialog AND AlertPopup.

const Qualification: React.FC = () => {
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();
  const dispatch = useDispatch<AppDispatch>();

  const qualifications = useSelector(selectQualificationsList);
  const totalCount = useSelector(selectSearchQualificationsCount);

  // Success/Error Selectors
  const createSuccess = useSelector(selectCreateQualificationSuccess);
  const createError = useSelector(selectCreateQualificationError);
  const updateSuccess = useSelector(selectUpdateQualificationSuccess);
  const updateError = useSelector(selectUpdateQualificationError);
  const deleteSuccess = useSelector(selectDeleteQualificationSuccess);
  const deleteError = useSelector(selectDeleteQualificationError);

  const PAGE_SIZE = 20;

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedQualification, setSelectedQualification] = useState<IQualification | null>(null);
  const [page, setPage] = useState(1);

  // Confirmation Dialog state
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  // Alert state
  const [alert, setAlert] = useState<{ message: string, severity: 'success' | 'error' } | null>(null);

  useEffect(() => {
    setActivePage('qualification');
    setPath([
      { title: 'Settings', path: '/master-data' },
      { title: 'Master Data', path: '/master-data' },
      { title: 'Qualification', path: '/master-data/qualifications' },
    ]);
    setIsShow(true);
    dispatch(searchQualifications({ search: '', page: 1, page_size: PAGE_SIZE }));
  }, [setActivePage, setPath, setIsShow, dispatch]);

  // Handle Notifications and Resets
  useEffect(() => {
    if (createSuccess) {
      setAlert({ message: 'Qualification created successfully', severity: 'success' });
      dispatch(searchQualifications({ search: '', page, page_size: PAGE_SIZE }));
      dispatch(resetQualificationState());
    }
    if (createError) {
      setAlert({ message: createError, severity: 'error' });
      dispatch(resetQualificationState());
    }

    if (updateSuccess) {
      setAlert({ message: 'Qualification updated successfully', severity: 'success' });
      dispatch(searchQualifications({ search: '', page, page_size: PAGE_SIZE }));
      dispatch(resetUpdateState());
    }
    if (updateError) {
      setAlert({ message: updateError, severity: 'error' });
      dispatch(resetUpdateState());
    }

    if (deleteSuccess) {
      setAlert({ message: 'Qualification deleted successfully', severity: 'success' });
      dispatch(searchQualifications({ search: '', page, page_size: PAGE_SIZE }));
      dispatch(resetDeleteState());
    }
    if (deleteError) {
      setAlert({ message: deleteError, severity: 'error' });
      dispatch(resetDeleteState());
    }
  }, [createSuccess, createError, updateSuccess, updateError, deleteSuccess, deleteError, dispatch, page]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    dispatch(searchQualifications({ search: '', page: newPage, page_size: PAGE_SIZE }));
  };

  const handleCreateClick = () => {
    setSelectedQualification(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (qualification: IQualification) => {
    setSelectedQualification(qualification);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (id: number) => {
    setDeleteId(id);
    setIsConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteId) {
      await dispatch(deleteQualification(deleteId));
      setIsConfirmOpen(false);
      setDeleteId(null);
    }
  };

  // Calculate total pages for pagination
  const totalPages = Math.ceil((totalCount || 0) / PAGE_SIZE);

  return (
    <Box>
      {alert && <AlertPopup message={alert.message} severity={alert.severity} open={!!alert} onClose={() => setAlert(null)} />}
      <MainContainer>
        <HeaderContainer>
          <PageTitle variant="h6">Manage Qualifications</PageTitle>
          <CreateButton variant="contained" disableElevation onClick={handleCreateClick}>
            Create Qualification
          </CreateButton>
        </HeaderContainer>

        <StyledTableContainer>
          <StyledTable>
            <StyledTableHead>
              <StyledTableRow>
                <StyledTableCell>Id</StyledTableCell>
                <StyledTableCell>Name</StyledTableCell>
                <StyledTableCell>Description</StyledTableCell>
                <StyledTableCell>Action</StyledTableCell>
              </StyledTableRow>
            </StyledTableHead>
            <TableBody>
              {qualifications && qualifications.length > 0 ? (
                qualifications.map((qualification: IQualification, index: number) => (
                  <StyledTableRow key={qualification.qualification_id}>
                    <StyledTableCell>{qualification.qualification_id}</StyledTableCell>
                    <StyledTableCell>{qualification.qualification_name}</StyledTableCell>
                    <StyledTableCell>{qualification.qualification_description}</StyledTableCell>
                    <StyledTableCell>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <ActionButton onClick={() => handleEditClick(qualification)}>
                          <EditIcon fontSize="small" />
                        </ActionButton>
                        <DeleteIconWrapper onClick={() => handleDeleteClick(qualification.qualification_id)}>
                          <DeleteIcon fontSize="small" />
                        </DeleteIconWrapper>
                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              ) : (
                <StyledTableRow>
                  <StyledTableCell colSpan={4} align="center">No qualifications found.</StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </StyledTable>
        </StyledTableContainer>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <CustomPagination
            totalPages={totalPages > 0 ? totalPages : 1}
            currentPage={page}
            onPageChange={handlePageChange}
          />
        </Box>
      </MainContainer>

      <QualificationModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
        qualification={selectedQualification}
      />

      <ConfirmationDialog
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Qualification"
        message="Are you sure you want to delete this qualification?"
      />
    </Box>
  );
};

export default Qualification;
